import React, { useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useDispatch } from 'react-redux'
import { useCookies } from 'react-cookie'
import { UserApi } from 'mvppoker-portal-common/api'
import { MVPTextInput } from '../../Views/TextInput/MVPTextInput'
import { MVPButton } from '../../Views/Buttons/MVPButton'
import { useNavigate } from '../navigation'
import { handleError, useApi } from '../../utils/useApi'
import { setActiveUser } from '../../state/reducers/activeUser'
import { VersionView } from '../../Views/VersionView/VersionView'
import { MVPLink } from '../../Views/Link/MVPLink'
import { FullPageHeader } from '../../Views/FullPageHeader/FullPageHeader'
import { setCredentialsAndUser } from '../../state/reducers/registeringUser'
import { setGameConfig } from '../../state/reducers/AppConfig'
import { env, whenEnv, whenEnvNot } from '../../config/env'
import { useRedirectLoggedIn } from '../../utils/redirect/useLoginRedirect'
import { makeActiveGame } from '../../state/reducers/activeGame'
import '../../index.scss'
import './LoginPage.scss'

interface LoginFormData {
	username: string
	password: string
}

export default () => {
	const form = useForm<LoginFormData>({
		mode: 'onSubmit',
	})
	const userApi = useApi(UserApi)
	const [isLoading, setLoading] = useState(false)
	const dispatch = useDispatch()
	const goToForgotPassword = useNavigate('forgotPassword')
	const goToRegistration = useNavigate('registrationCredentials')
	const goToEmailConfirmation = useNavigate('registrationConfirmationEmail')
	const goToPublicGames = useNavigate('publicGames')

	const [cookies, setCookie] = useCookies(['latestVersionUpdate'])

	const latestVersion = cookies.latestVersionUpdate
	if (!latestVersion || Date.now() - 24*60*60*1000 > latestVersion){
		setCookie('latestVersionUpdate', Date.now())
		window.location.replace(window.location.href)
	}

	const redirect = useRedirectLoggedIn()
	if (redirect) {
		return redirect
	}
	
	const onSubmit: SubmitHandler<LoginFormData> = (formData) => {
		setLoading(true)

		userApi
			.authUserLogin(formData)
			.then(({ data }) => {
				setLoading(false)
				dispatch(setGameConfig(data.config))
				dispatch(setActiveUser(data))
				const activeRoom = data.currentGames[0]
				const userId = data.user.id
				if (activeRoom && userId){
					dispatch(makeActiveGame({ ...activeRoom, userId }))
				}
			})
			.catch((err) => {
				setLoading(false)
				if (err.response?.status === 403) {
					if (err.response?.data.message === 'User is disabled'){
						toast.error('Your access has been disabled for 24hrs.\nPlease come again tomorrow.')
						return
					}
					dispatch(
						setCredentialsAndUser({
							credentials: {
								name: '',
								email: form.getValues().username,
								password: form.getValues().password,
								referral: '',
							},
						}),
					)
					goToEmailConfirmation()
				} else {
					handleError(err)
				}
			})
	}

	const onClickRegister = () => {
		goToRegistration()
	}
	const onClickForgotPassword = () => {
		goToForgotPassword()
	}
	const onClickPublicGames = () => {
		goToPublicGames()
	}
	return (
		<div id='new-login-page' className='stretch-page'>
			<VersionView />
			<FormProvider {...form}>
				<form id='middle-col' onSubmit={form.handleSubmit(onSubmit)}>
					<FullPageHeader title='Login' />
					<MVPTextInput
						formField='username'
						formValidation={{ required: 'Email required!' }}
						label='Email'
						placeholder='Enter your email'
						type='email'
					/>
					<div style={{ height: '20px' }} />
					<MVPTextInput
						formField='password'
						formValidation={{
							required: 'Password required!',
							validate: {
								has8Chars: (it: string) => it.length >= 8 || 'Minimum 8 characters',
								hasLessThan100Char: (it: string) => it.length < 100 || 'Maximum 8 characters',
								has1Uppercase: (it: string) => !![...it].find((it) => it >= 'A' && it <= 'Z') || 'Needs one uppercase letter',
								has1Number: (it: string) => !![...it].find((it) => it >= '0' && it <= '9') || 'Needs one number',
								has1SpecialChar: (it: string) =>
									!![...it].find((it) => '^$*.[]{}()?-"!@#%&/\\,><\':;|_~`+='.includes(it)) ||
									'Needs at least one special character (^$*.[]{}()?-"!@#%&/\\,><\':;|_~`+=)',
							},
						}}
						label='Password'
						placeholder='Enter your password'
						type='password'
					/>
					<MVPLink id='forgot-password-link' onClick={onClickForgotPassword}>
						Forgot your password?
					</MVPLink>
					<MVPButton type='submit' id='login-button' loading={isLoading}>
						Login
					</MVPButton>

					{whenEnv('prod', true) &&
						<MVPLink id='register-link' onClick={onClickRegister}>
							Dont have an account? <b>Register here</b>
						</MVPLink>
					}
				</form>
			</FormProvider>

			<MVPButton id='publicgames-button' onClick={onClickPublicGames}>
				Want To See Our Live Games?
			</MVPButton>
		</div>
	)
}
